import React from 'react';
import { useNavigate } from 'react-router-dom';
import Sym from '../components/Sym';

/**
 * Hero del rediseño: columna de texto (eyebrow + H1 serif con acento azul +
 * subtítulo + CTAs + badges de confianza) y columna visual con tarjetas mockup
 * flotantes (recibo, badge "Del SAT", mini-portal).
 *
 * Decisión de negocio: prueba CON tarjeta al inicio → los badges NO dicen
 * "sin tarjeta"; usamos "15 días de prueba / Listo en 2 minutos / Cancela cuando quieras".
 */
const Hero2 = () => {
  const navigate = useNavigate();
  const badge = (icon, text) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: "500 .95rem 'Hanken Grotesk'", color: 'var(--muted-3)' }}>
      <Sym name={icon} size={19} color="#6FA0FF" />{text}
    </span>
  );

  return (
    <section style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Glow de fondo */}
      <div aria-hidden style={{ position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 900, height: 620, background: 'radial-gradient(circle,var(--well-glow),transparent 62%)', filter: 'blur(8px)', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto', padding: '64px clamp(20px,5vw,40px) 88px', display: 'flex', flexWrap: 'wrap', gap: 'clamp(32px,5vw,64px)', alignItems: 'center' }}>
        {/* Columna texto */}
        <div style={{ flex: '1 1 440px', minWidth: 290 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 999, background: 'rgba(47,107,255,.12)', border: '1px solid rgba(47,107,255,.28)', animation: 'ls2-fadeUp .5s both' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6FA0FF', boxShadow: '0 0 10px #6FA0FF' }} />
            <span style={{ font: "700 .78rem 'IBM Plex Mono'", letterSpacing: '.09em', textTransform: 'uppercase', color: '#8FB4FF' }}>Recibos de nómina, sin vueltas</span>
          </div>

          <h1 style={{ font: "600 clamp(2.6rem,6vw,4.7rem)/1.02 'Source Serif 4'", letterSpacing: '-.025em', color: 'var(--heading)', margin: '20px 0 0', textWrap: 'balance', animation: 'ls2-fadeUp .6s both .05s' }}>
            Tus recibos de nómina, entregados <span style={{ fontStyle: 'italic', color: '#6FA0FF' }}>solos.</span>
          </h1>

          <p style={{ font: "400 clamp(1.08rem,1.6vw,1.32rem)/1.6 'Hanken Grotesk'", color: 'var(--body)', margin: '22px 0 0', maxWidth: 548, animation: 'ls2-fadeUp .6s both .12s' }}>
            PaySlip descarga del SAT los recibos que ya timbraste —con tu e.firma— y se los entrega a administradores y colaboradores. Tú no mueves un dedo. Pruébalo <strong style={{ color: 'var(--heading)' }}>15 días gratis</strong>.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 32, animation: 'ls2-fadeUp .6s both .18s' }}>
            <a onClick={() => navigate('/register')} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#2F6BFF', color: '#fff', font: "700 1.05rem 'IBM Plex Mono'", padding: '16px 26px', borderRadius: 13, cursor: 'pointer', boxShadow: '0 14px 34px -10px rgba(47,107,255,.8)' }}>
              Comienza tu prueba de 15 días<Sym name="arrow_forward" size={20} />
            </a>
            <a onClick={() => navigate('/caracteristicas')} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'var(--ghost-bg)', color: 'var(--heading)', font: "700 1.05rem 'IBM Plex Mono'", padding: '16px 24px', borderRadius: 13, cursor: 'pointer', border: '1.5px solid var(--border-soft)' }}>
              <Sym name="play_circle" size={20} color="#6FA0FF" />Ver cómo funciona
            </a>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 26, animation: 'ls2-fadeUp .6s both .24s' }}>
            {badge('check_circle', '15 días de prueba')}
            {badge('check_circle', 'Listo en 2 minutos')}
            {badge('check_circle', 'Cancela cuando quieras')}
          </div>
        </div>

        {/* Columna visual: tarjetas mockup flotantes */}
        <div style={{ flex: '1 1 400px', minWidth: 290, position: 'relative', height: 'clamp(400px,42vw,470px)', animation: 'ls2-fadeIn .8s both .2s' }}>
          {/* Recibo (claro, rotado) */}
          <div style={{ position: 'absolute', top: '14%', left: '6%', width: 'min(340px,82%)', background: '#fff', borderRadius: 20, padding: 24, boxShadow: '0 40px 80px -30px rgba(0,0,0,.55)', transform: 'rotate(-3deg)', animation: 'ls2-floatY 7s ease-in-out infinite' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.08em', textTransform: 'uppercase', color: '#8A8477' }}>Recibo de nómina</span>
              <span style={{ font: "600 .72rem 'Hanken Grotesk'", color: '#8A8477' }}>Quincena 14</span>
            </div>
            <div style={{ height: 11, width: '62%', background: '#ECE7DB', borderRadius: 6, marginBottom: 9 }} />
            <div style={{ height: 11, width: '40%', background: '#F1ECE1', borderRadius: 6, marginBottom: 18 }} />
            {[['Percepciones', '$18,400.00'], ['Deducciones', '$3,120.00']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderTop: '1px solid #F0EBE0' }}>
                <span style={{ font: "500 .92rem 'Hanken Grotesk'", color: '#6B675E' }}>{k}</span>
                <span style={{ font: "600 .92rem 'Hanken Grotesk'", color: '#3A3730' }}>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '14px 0 4px', borderTop: '2px solid #0E1A30', marginTop: 4 }}>
              <span style={{ font: "700 .95rem 'Hanken Grotesk'", color: '#0E1A30' }}>Neto a pagar</span>
              <span style={{ font: "700 1.15rem 'Source Serif 4'", color: '#0E1A30' }}>$15,280.00</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, padding: '10px 12px', background: '#EAF0FF', borderRadius: 11 }}>
              <Sym name="cloud_done" size={20} color="#2559E0" />
              <span style={{ font: "600 .82rem 'Hanken Grotesk'", color: '#2559E0' }}>Descargado del SAT · Folio A4F9-22</span>
            </div>
          </div>

          {/* Badge "Del SAT" (oscuro) */}
          <div data-dark style={{ position: 'absolute', top: '2%', right: '4%', background: 'var(--surface)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', borderRadius: 16, padding: '14px 18px', boxShadow: '0 20px 44px -16px rgba(0,0,0,.7)', animation: 'ls2-pulse 3.4s ease-in-out infinite', zIndex: 3 }}>
            <div style={{ font: "700 .68rem 'IBM Plex Mono'", letterSpacing: '.08em', textTransform: 'uppercase', color: '#6FA0FF' }}>Del SAT</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
              <Sym name="cloud_download" size={22} color="#6FA0FF" />
              <span style={{ font: "700 1rem 'Hanken Grotesk'", color: '#fff' }}>2,192 recibos</span>
            </div>
          </div>

          {/* Mini-portal (claro) — sólo desktop */}
          <div className="ls2-hero-portal" style={{ position: 'absolute', bottom: '2%', right: '2%', width: 186, background: '#fff', borderRadius: 22, padding: 14, boxShadow: '0 30px 60px -22px rgba(0,0,0,.55)', animation: 'ls2-floatY2 6s ease-in-out infinite' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: '#EAF0FF', color: '#2F6BFF', display: 'grid', placeItems: 'center' }}>
                <Sym name="smartphone" size={16} color="#2F6BFF" />
              </span>
              <span style={{ font: "700 .74rem 'Hanken Grotesk'", color: '#2A2A2A' }}>Mi portal</span>
            </div>
            {[52, 44].map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#F4F6FB', borderRadius: 10, marginBottom: i === 0 ? 7 : 0 }}>
                <div>
                  <div style={{ height: 7, width: w, background: '#D6DEEC', borderRadius: 4, marginBottom: 5 }} />
                  <div style={{ height: 6, width: w - 14, background: '#E7ECF4', borderRadius: 4 }} />
                </div>
                <Sym name="download" size={16} color="#2F6BFF" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero2;
